import { useQuery } from '@tanstack/react-query';
import GraphqlService from '../service/graphqlService';

export type ExportAction =
  | 'brandConsumptions'
  | 'machineTelemetryConsumption'
  | 'telemetryObject'
  | 'lifeCycleObject'
  | 'sandboxObject';

export interface UseExportEstimateArgs {
  projectId: number;
  action: ExportAction;
  dateIni: string;
  dateEnd: string;
  filters?: string;
  serialNumber?: string;
  object?: string;
  queryParams?: string;
  totalRows?: number | null;
  enabled?: boolean;
}

export interface ExportEstimateData {
  __typename: 'EstimateExportDurationSuccess';
  action: ExportAction;
  estimatedSeconds: number;
  willExceedLimit: boolean;
  limitSeconds: number;
}

export interface UseExportEstimateResult {
  isLoading: boolean;
  isError: boolean;
  data: ExportEstimateData | undefined;
  error: Error | null;
}

/**
 * Pre-flight estimate hook for the 5 CSV export actions (feature 017).
 *
 * Wraps the `estimateExportDuration` GraphQL query. Per FR-015 caching is
 * disabled — every probe corresponds to a specific in-flight export decision.
 * Non-success union members (BadRequest, InternalServerError, NotFound,
 * Forbidden) are surfaced as `isError: true` so the modal can render the
 * yellow warning banner (FR-019).
 */
export default function useExportEstimate(args: UseExportEstimateArgs): UseExportEstimateResult {
  const {
    projectId, action, dateIni, dateEnd,
    filters, serialNumber, object, queryParams, totalRows,
    enabled = true,
  } = args;

  const query = useQuery<ExportEstimateData, Error>(
    ['exportEstimate', projectId, action, dateIni, dateEnd, filters, serialNumber, object, queryParams, totalRows],
    async () => {
      const res = await GraphqlService.estimateExportDuration({
        projectId, action, dateIni, dateEnd,
        filters, serialNumber, object, queryParams, totalRows,
      });
      if (!res || res.__typename !== 'EstimateExportDurationSuccess') {
        throw new Error(res?.message || 'Could not estimate export duration');
      }
      return res as ExportEstimateData;
    },
    {
      enabled: Boolean(enabled && projectId && action && dateIni && dateEnd),
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  return {
    isLoading: query.isLoading || query.isFetching,
    isError: query.isError,
    data: query.data,
    error: (query.error as Error) ?? null,
  };
}
